// Supplier notes to review (Lane 1344, Phase 3 slice 3B; PLAN section 12). A note
// can change what an offer means ("8 weeks if we get the full group"). Wingman does
// not read supplier text: every note counts as not reviewed until you say it is not
// a condition, or mark it an open condition. An open condition is cleared only by a
// confirmed rule of yours. A new submission needs a new review.
import React, { useCallback, useEffect, useState } from 'react';
import { ruleText } from './scenarioText';

const STATE_TEXT = {
  unreviewed: 'Not reviewed', not_a_condition: 'Not a condition', open: 'Open condition',
  cleared: 'Cleared by a rule', open_rule_changed: 'Open condition (the rule that cleared it changed)',
};

// ``supplierId`` (Lane 1395): only that supplier's notes, inside the Supplier responses
// pop-up; ``onReviewed`` tells the table to recount.
export default function NotesReview({ api, eventId, names, currency, refreshKey, labelOf, supplierId, onReviewed }) {
  const [notes, setNotes] = useState(null);
  const [rules, setRules] = useState([]);
  const [reason, setReason] = useState({});
  const [ruleFor, setRuleFor] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => Promise.all([api.supplierNotes(eventId), api.rules(eventId)])
    .then(([n, r]) => { setNotes(n.items); setRules(r.items.filter((x) => x.state === 'confirmed')); })
    .catch((e) => setError(e.message)), [api, eventId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const review = async (n, state) => {
    setBusy(true);
    setError('');
    const key = `${n.offer_id}|${n.part || ''}`;
    try {
      await api.reviewNote(eventId, { offer_id: n.offer_id, material_number: n.part, state,
        reason: reason[key] || null, rule_id: state === 'cleared' ? ruleFor[key] || null : null,
        expected_review_seq: n.review_seq });
      await load();
      if (onReviewed) onReviewed();
    } catch (e) {
      if (e.code === 'stale_review') await load();
      setError(e.message);
    } finally { setBusy(false); }
  };

  const shown = (notes || []).filter((n) => supplierId === undefined || Number(n.supplier_id) === Number(supplierId));
  if (notes && shown.length === 0 && supplierId !== undefined) return <div className="srce-mut">No notes from this supplier.</div>;
  if (!notes || shown.length === 0) return error ? <div className="srce-error" role="alert">{error}</div> : null;
  return (
    <div className={supplierId === undefined ? 'srce-card srce-gap' : 'srce-stack'}>
      {supplierId === undefined && (
        <div className="srce-card-t">Supplier notes to review ({shown.filter((n) => n.state === 'unreviewed').length} not reviewed)</div>
      )}
      <div className="srce-sub">A note is the supplier's own words. Scenario results that use these offers say so until you review them.</div>
      {error && <div className="srce-error" role="alert">{error}</div>}
      {shown.map((n) => {
        const key = `${n.offer_id}|${n.part || ''}`;
        return (
          <div key={key} className="srce-col">
            <span><span className="srce-strong">{names[String(n.supplier_id)] || 'Supplier'}</span>, {n.part ? `part ${n.part}` : 'whole offer'}:
              {' '}<q>{n.text}</q></span>
            <span className="srce-sub">{STATE_TEXT[n.state] || n.state}{n.reason ? `: ${n.reason}` : ''}</span>
            <span className="srce-row">
              <input className="srce-input" placeholder="Your reason (needed for Not a condition)" maxLength={1000} value={reason[key] || ''}
                onChange={(e) => setReason({ ...reason, [key]: e.target.value })} />
              <button type="button" className="srce-btn srce-btn--quiet" disabled={busy || !(reason[key] || '').trim()}
                onClick={() => review(n, 'not_a_condition')}>Not a condition</button>
              <button type="button" className="srce-btn srce-btn--quiet" disabled={busy} onClick={() => review(n, 'open')}>Open condition</button>
              {['open', 'open_rule_changed'].includes(n.state) && rules.length > 0 && (
                <>
                  <select className="srce-input" aria-label="Rule that models it" value={ruleFor[key] || ''}
                    onChange={(e) => setRuleFor({ ...ruleFor, [key]: e.target.value })}>
                    <option value="">A confirmed rule that models it…</option>
                    {rules.map((r) => <option key={r.rule_id} value={r.rule_id}>{ruleText(r, names, currency, labelOf)}</option>)}
                  </select>
                  <button type="button" className="srce-btn srce-btn--quiet" disabled={busy || !ruleFor[key]} onClick={() => review(n, 'cleared')}>Cleared by this rule</button>
                </>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
